/**
 * RevokeDeviceLanding — public landing for the one-click "Wasn't me —
 * revoke this device" CTA in the new-country alert email.
 *
 * Flow:
 *   1. User clicks the email link → lands here with `?token=...`.
 *   2. We immediately POST the token to `/api/auth/devices/revoke-by-link`.
 *   3. On 200 → render a clean confirmation (with masked email + country
 *      code + "go to login" CTA).
 *   4. On 4xx → render a friendly error (expired / already-used / invalid)
 *      with a link back to /forgot-password so they can rotate creds.
 *
 * No auth cookie needed — the token authorises the action.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, KeyRound, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export default function RevokeDeviceLanding() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('loading');   // loading | ok | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const fired = useRef(false);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the one-shot mount fetch. */
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!token) { setStatus('error'); setError('Missing token.'); return; }
    axios.post(`${API_BASE}/auth/devices/revoke-by-link`, { token })
      .then(({ data: r }) => { setData(r); setStatus('ok'); })
      .catch((e) => {
        setError(e?.response?.data?.detail || 'This revoke link is no longer valid.');
        setStatus('error');
      });
  }, [token]);
  /* eslint-enable */

  return (
    <main
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{ background: '#0A0A0A' }}
      data-testid="revoke-device-landing"
    >
      <div
        className="w-full max-w-lg rounded-lg border p-8"
        style={{
          background: '#121212',
          borderColor: '#1F1F1F',
          fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
        }}
      >
        {status === 'loading' && (
          <div className="flex flex-col items-center text-center" data-testid="revoke-loading">
            <Loader2 size={28} className="animate-spin text-[#D4AF37]" />
            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              [REVOKING DEVICE…]
            </p>
            <p className="mt-2 text-sm text-white/55">
              Killing the suspicious session. One moment.
            </p>
          </div>
        )}

        {status === 'ok' && (
          <div data-testid="revoke-success">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
              [DEVICE · REVOKED]
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <ShieldCheck size={20} className="text-emerald-400" />
              </div>
              <h1 className="text-2xl font-serif text-white leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Done. That session is dead.
              </h1>
            </div>
            <p className="mt-4 text-sm text-white/65 leading-relaxed">
              We revoked the device that signed in from{' '}
              {data?.country_code && (
                <span className="px-1.5 py-0.5 rounded-sm border bg-[rgba(174,82,234,0.10)] text-[#C084FC] border-[rgba(174,82,234,0.30)] text-[10px] tracking-wide font-bold">
                  {data.country_code}
                </span>
              )}
              {' '}for{' '}
              <span className="text-white font-semibold">{data?.masked_email}</span>.
              Your other devices stay signed in.
            </p>

            <div className="mt-6 rounded-md border border-rose-500/30 bg-rose-500/[0.05] p-4">
              <div className="flex items-start gap-3">
                <KeyRound size={16} className="text-rose-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-rose-400 font-bold">
                    [RECOMMENDED · CHANGE PASSWORD]
                  </div>
                  <p className="mt-1 text-xs text-white/65 leading-relaxed">
                    If you didn&apos;t expect this sign-in, your password may
                    be compromised. Reset it so the attacker can&apos;t come back.
                  </p>
                  <Link
                    to="/forgot-password"
                    data-testid="revoke-reset-password-link"
                    className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-rose-300 hover:text-rose-200"
                  >
                    Reset password <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/login"
              data-testid="revoke-back-to-login"
              className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-white/80 hover:text-white border-b border-white/20 hover:border-white/60 pb-1"
            >
              Back to sign-in <ArrowRight size={12} />
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="revoke-error">
            <div className="text-[10px] uppercase tracking-[0.22em] text-rose-400 font-bold">
              [LINK · UNUSABLE]
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center">
                <AlertTriangle size={20} className="text-rose-400" />
              </div>
              <h1 className="text-2xl font-serif text-white leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                We couldn&apos;t use this link.
              </h1>
            </div>
            <p className="mt-4 text-sm text-white/65 leading-relaxed">
              {error}
            </p>
            <p className="mt-3 text-xs text-white/45 leading-relaxed">
              The fastest way to lock down your account is to sign in and
              revoke devices manually from your Profile page — or rotate
              your password.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/login"
                data-testid="revoke-error-login"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold rounded border border-white/20 text-white hover:bg-white/5"
              >
                Sign in <ArrowRight size={12} />
              </Link>
              <Link
                to="/forgot-password"
                data-testid="revoke-error-reset"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold rounded border bg-rose-500/10 text-rose-300 border-rose-500/40 hover:bg-rose-500/20"
              >
                Reset password <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/10 text-[9px] uppercase tracking-[0.22em] text-white/35 text-center font-bold">
          Vigilant Entities · Sovereign AI · Zero-leak architecture
        </div>
      </div>
    </main>
  );
}
